import React, { useEffect, useState } from "react";
import { Table, Dropdown, Menu } from "antd";
import { Checkbox } from "@vds/checkboxes";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchRequest,
  downloadRequest,
  singleUpdateRequest,
  updatePaginationRequest,
} from "../../../store/actions/alerts";
import { Box } from "@mui/material";
import { dateFormatWithAt, dateFormatWithSubject } from "../../../global/utility/dateFormat";
import ToastNotification from "../../../global/components/ToastNotification";
import dayjs from "dayjs";
import {
  DropdownFilterWithCheckboxAndInitialValue,
  DropdownFilterWithSearchAndCheckbox,
  DropdownFilterWithDateRangePicker,
  DropdownFilterWithSearch,
} from "../../../components/DropdownFilter";
import AlertsDetails from "./AlertsDetails";
import BulkDetails from "./BulkDetails";

const AlertsList = () => {
  const locationObj = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { deviceId, deviceName } = locationObj.state || {
    deviceId: "",
    organization: "",
    locationId: "",
    shipmentname: "",
  };

  const [showNotification, setShowNotification] = useState(false);
  const [notificationProps, setNotificationProps] = useState({});
  // Redux state selectors
  const user = useSelector((state) => state.auth.user);
  const isLoading = useSelector((state) => state.alert.isLoading);
  const alertCounts = useSelector((state) => state.alert.alertStatusCounts);
  const filters = useSelector((state) => state.alert.filters);
  const alerts = useSelector((state) => state.alert.data.alerts);
  const statuses = useSelector((state) => state.alert.data.statuses);
  const levels = useSelector((state) => state.alert.data.levels);
  const devices = useSelector((state) => state.alert.data.devices);
  const notification = useSelector((state) => state.alert.notification);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isbulkPopupVisible, setIsBulkPopupVisible] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]); // State to manage selected items
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const InitialStatus = useSelector((state) => state.alert.data.status);

  useEffect(() => {
    setSelectedItems([]);
    setSelectedStatuses([]);
  },[user?.accountId])

  const handleCheckboxChange = (checked, recordId,text) => {
    if (checked) {
      setSelectedItems((prevItems) => [...prevItems, { id: recordId }]);
      setSelectedStatuses((prevStatuses) => [...prevStatuses, text]);
    } else {
      setSelectedItems((prevItems) =>
        prevItems.filter((item) => item.id !== recordId)
      );
      setSelectedStatuses((prevStatuses) =>
        prevStatuses.filter((status) => status !== text)
      );
    }
  };

  const handleTableChange = (pagination) => {
    if (
      Object.hasOwn(pagination, "current") &&
      filters.pagenumber !== pagination.current - 1
    ) {
      dispatch(
        fetchRequest({ ...filters, pagenumber: pagination.current - 1 })
      );
    }
  };

  const handleStatusFilterChange = (val) => {
    const filterArr = Object.entries(val.filters);

    if (filterArr.length === 0) {
      return;
    }

    const filterValue = filterArr.map((filter) => ({
      text: filter[0],
      value: filter[1],
    }));
    let order = val.isAsc ? "asc" : "desc";
    let selectedFilters = val.filters || {};

    dispatch(
      fetchRequest({
        ...filters,
        status: filterValue,
      })
    );
  };

  const handleStatusFilterReset = () => {
    dispatch(
      fetchRequest({
        ...filters,
        status: InitialStatus,
      })
    );
  };

  const handleLevelFilterChange = (val) => {
    let order = val.isAsc ? "asc" : "desc";
    let field = "severity";
    dispatch(
      fetchRequest({
        ...filters,
        sort: [{ field, order }],
        level: val.filters || {},
      })
    );
  };

  const handleLevelFilterReset = (val) => {
    let order = val.isAsc ? "asc" : "desc";
    let field = "severity";
    dispatch(
      fetchRequest({
        ...filters,
        sort: [{ field, order }],
        level: "",
      })
    );
  };
  const handleDeviceFilterChange = (val) => {
    let order = val.isAsc ? "asc" : "desc";
    let deviceFilter = val?.filters?.text?.length > 0 ? val?.filters?.text : "";
    dispatch(
      fetchRequest({
        ...filters,
        sort: [{ field: "$/metadata/device-name", order }],
        device: deviceFilter,
      })
    );
  };

  const handleDeviceFilterReset = () => {
    dispatch(fetchRequest({ ...filters, device: [] }));
    navigate(locationObj.pathname,{replace:true})
  };

  const handleLastUpdatedFilterChange = (val) => {
    let startDate = "";
    let endDate = "";

    if (val?.filters?.text?.length > 0) {
      const [start, end] = val.filters.text;
      // startDate = dayjs(start).format("YYYY-MM-DD");
      // endDate = dayjs(end).format("YYYY-MM-DD");
       startDate = start;
       endDate =  end? dayjs(end)
                                 .set("hour", 23)
                                 .set("minute", 59)
                                 .set("second", 59)
                                 : ""
    }

    const order = val.isAsc ? "asc" : "desc";
    dispatch(
      fetchRequest({
        ...filters,
        sort: [{ field: "lastupdated", order }],
        lastUpdated:
         val?.filters?.text?.length > 0
            ? [startDate, endDate]
            : "",
          // val?.filters?.text?.length > 0
          //   ? [dayjs(startDate), dayjs(endDate)]
          //   : "",
      })
    );
  };

  const handleLastUpdatedFilterReset = () => {
    dispatch(fetchRequest({ ...filters, lastUpdated: "" }));
  };

  const handleSubjectSearch = (val) => {
    let order = val.isAsc ? "asc" : "desc";
    let subjectFilter = "";
    if (val.filteredResult && val.filteredResult.length > 0) {
      subjectFilter = val.filteredResult;
    } else if (val.filters?.text) {
      subjectFilter = val.filters.text;
    }

    dispatch(
      fetchRequest({
        ...filters,
        sort: [{ field: "name", order }],
        subject: subjectFilter,
      })
    );
  };

  const handleSubjectSearchReset = () => {
    dispatch(
      fetchRequest({
        ...filters,
        subject: "",
      })
    );
  };

  const handleDownload = () => {
    try {
      dispatch(downloadRequest("organizationList"));
      setNotificationProps({
        type: "success",
        message: "Export",
        description: "Export completed successfully.",
        duration: 1,
        style: { backgroundColor: "#d4edda", color: "#155724" },
      });
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
      }, 1000);
    } catch (error) {
      setNotificationProps({
        type: "error",
        message: "Export",
        description: error.message || "Unable to serialize unrecognized input.",
        duration: 5,
        style: { backgroundColor: "#FFECE0", color: "black" },
      });
      setShowNotification(true);
      setTimeout(() => {
        setShowNotification(false);
      }, 3000);
    }
  };

  const editAlerts = (record) => {
    if (record) {
      dispatch(
        singleUpdateRequest({
          current: record,
          fields: {
            status: record?.status,
            comment: record?.lastComment,
          },
        })
      );
      setIsPopupVisible(true);
    }
  };

  const bulkeditAlerts = (record) => {
    if (record) {
      setIsBulkPopupVisible(true);
    }
  };

  function capitalizeFirstLetter(name) {
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  }

  const handleAssociationSearch = (val) => {
    let order = val.isAsc ? "asc" : "desc";
    let associationFilter = "";
    if (val.filteredResult && val.filteredResult.length > 0) {
      associationFilter = val.filteredResult;
    } else if (val.filters?.text) {
      associationFilter = val.filters.text;
    }
    dispatch(
      fetchRequest({
        ...filters,
        sort: [{ field: "createdon", order }],
        associationname: associationFilter,
      })
    );
  };

  const handleAssosciationSearchReset = () => {
    dispatch(
      fetchRequest({
        ...filters,
        sort: [{ field: "createdon", order: "desc" }],
        associationname: "",
      })
    );
  };

  const tableData = alerts.map((val) => {
    const batteryCriticalValue = val?.condition?.startsWith(
      "$[?(@.eventMsg.fields.battery <"
    )
      ? val.condition
        .replaceAll("$[?(@.eventMsg.fields.battery <", "")
        .replaceAll(")]", "")
        .trim()
      : "N/A";

    let acknowledged = null;
    if (!val?.state) {
      if (val.isacknowledged === false && val.iscleared === false) {
        acknowledged = "New";
      } else if (val.isacknowledged === true && val.iscleared === false) {
        acknowledged = "Acknowledged";
      } else if (val.isacknowledged === false && val.iscleared === true) {
        acknowledged = "Resolved";
      }
    } else {
      acknowledged = "Auto Resolved";
    }


    return {
      key: val?.id,
      billingaccountid: val?.billingaccountid,
      value: val,
      lastupdated: val.lastupdated,
      batteryLevel: `Battery Level Below ${batteryCriticalValue}%`,
      batteryValue: val?.metadata?.battery,
      batteryCriticalValue,
      siteId: val?.metadata?.siteId,
      locationName:
        val?.metadata?.locationName || "device_bulkupdate_dummy_tag",
      orgId: val?.metadata?.orgId,
      associationname:
        (val?.metadata?.shipmentname && val?.metadata?.shipmentname !== "N/A")
          ? val?.metadata?.shipmentname
          : val?.metadata?.assetname || "device_bulkupdate_dummy_tag",
      isacknowledged: acknowledged,
      condition: capitalizeFirstLetter(val.severity),
      lastComment:
        val?.metadata?.audit?.[val.metadata.audit.length - 1]?.comment,
      name:
        val.condition === "$[?(@.eventMsg.fields.battery < 10)]"
          ? "Battery Level Below 10%"
          : "Battery Level Below 20%",
      device: val.metadata["device-name"],
      deviceid: val?.deviceid,
      action: (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item>
                {user?.role?.permissions?.alarms?.view && (
                  <div
                    role="button"
                    className="dropdown-item"
                    onClick={() =>{
                      editAlerts({
                        id: val?.id,
                        record: val,
                        billingaccountid: val?.billingaccountid,
                        name: val?.name,
                        msg: `Battery Level Below ${batteryCriticalValue}%`,
                        batteryCriticalValue: val.condition.includes("<")
                          ? "Minimum"
                          : "Maximum",
                        criticalValue: val.name.split(/<|>/)[1]?.replace(/[^0-9.]/g, ""),
                        batteryValue: val?.metadata['eventMsg']?.fields,
                        siteId: val?.metadata?.siteId,
                        locationName: val?.metadata?.locationName,
                        orgId: val?.metadata?.orgId,
                        organization: val?.metadata?.organizationName,
                        device: val?.metadata?.devicename,
                        level: val?.severity?.charAt(0)?.toUpperCase() + val?.severity?.slice(1),
                        status: acknowledged,
                        lastupdated: val?.lastupdated,
                        createdon: val?.createdon,
                        shipment: val?.metadata?.shipmentname,
                        alertgroup: val?.metadata["contact-group"],
                        lastComment:
                          val?.metadata?.audit?.[val.metadata.audit.length - 1]
                            ?.comment,
                      })
                    }
                    }
                  >
                    View Alert
                  </div>
                )}
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <button
            className="btn btn-link text-secondary"
            onClick={(e) => e.preventDefault()}
          >
            <i className="bi-three-dots-vertical fs-6" />
          </button>
        </Dropdown>
      ),
    };
  });
  const updatedStatus = (val) => {
    let acknowledged = null;
    if (!val?.state) {
      if (val.isacknowledged === false && val.iscleared === false) {
        acknowledged = "New";
      } else if (val.isacknowledged === true && val.iscleared === false) {
        acknowledged = "Acknowledged";
      } else if (val.isacknowledged === false && val.iscleared === true) {
        acknowledged = "Resolved";
      }
    } else {
      acknowledged = "Auto Resolved";
    }
    return acknowledged;
  };
  const columns = [
    {
      title: (
        <DropdownFilterWithCheckboxAndInitialValue
          text="Status"
          onApply={handleStatusFilterChange}
          onReset={handleStatusFilterReset}
          menuItems={statuses}
        />
      ),
      dataIndex: "isacknowledged",
      key: "isacknowledged",
      width: "19%",
      render: (text, record) => (
        <div className="d-flex flex-row justify-content-start align-items-center">
          {text !== "Auto Resolved" && text !== "Resolved" ? (
            <Checkbox
              key={record.id}
              selected={selectedItems.some((item) => item.id === record.key)}
              className="me-3"
              onChange={(e) =>
                handleCheckboxChange(e.target.checked, record.key,text)
              }>
              {text}
            </Checkbox>
          ) : (
            <Box style={{ marginLeft: (text === "Auto Resolved" || text === "Resolved") && 30 }}>
              {text}
            </Box>
          )}
        </div>
      ),
    },
    {
      title: (
        <DropdownFilterWithCheckboxAndInitialValue
          text="Severity"
          //isSortable={true}
          onApply={handleLevelFilterChange}
          onReset={handleLevelFilterReset}
          menuItems={levels.map((level) => ({
            text: level.label,
            value: level.value,
          }))}
        />
      ),
      dataIndex: "condition",
      key: "condition",
      className: "alert-severity",
      width: "10%",
      render: (text) => (
        <Box
          className={`${
            text === "Warning" ||  text?.toLowerCase() === "major" ||
            text === "Error" ||
            (text === "Critical" && "font-face-vzw")
          } ${
            text === "Error" || text === "Critical"
              ? "alarm-condition-error"
              : text === "Warning" || text?.toLowerCase() === "major"
                ? "alarm-condition"
                : ""
            }`}
          style={{
            display: "inline-block",
            padding: "1px 5px",
            borderRadius: "2px",
            textAlign: "center",
          }}
        >
          {text === "Error" || text === "Critical"
            ? "Critical"
            : text === "Warning" || text?.toLowerCase() === "major"
              ? "Warning"
            : text?.toLowerCase() === "minor"? "Notification":text}
        </Box>
      ),
    },
    {
      title: (
        <DropdownFilterWithSearchAndCheckbox
          text="Subject"
          isSortable={true}
          onApply={handleSubjectSearch}
          onReset={handleSubjectSearchReset}
        />
      ),
      dataIndex: "batteryLevel",
      key: "batteryLevel",
      width: "25%",
      render: (text, record) => {
        let subject = record?.value?.name || "";
        console.log("subject",record)
        if(subject && subject?.match(/\d{4}-\d{2}-\d{2}/gi)){
          const [text, dateString] = subject.split(" at");
          subject= `${text} at ${dateFormatWithSubject(dateString?.trim())}`;
        }

        return (
          <button
            className="amt-btn-link"
            onClick={() =>{
              editAlerts({
                id: record?.value?.id,
                record: record?.value,
                billingaccountid: record?.value?.billingaccountid,
                name: subject,
                msg: `Battery Level Below 10%`,
                batteryCriticalValue: record.value?.condition.includes("<")
                  ? "Minimum"
                  : "Maximum",
                criticalValue: record.value?.name.split(/<|>/)[1]?.replace(/[^0-9.]/g, ""),
                batteryValue: record?.value?.metadata['eventMsg']?.fields,
                siteId: record?.value?.metadata?.siteId,
                locationName: record?.value?.metadata?.locationName,
                orgId: record?.value?.metadata?.orgId,
                organization: record?.value?.metadata?.organizationName,
                device: record?.value?.metadata?.devicename,
                level:  record?.value?.severity?.charAt(0)?.toUpperCase() + record?.value?.severity?.slice(1),
                status: updatedStatus(record.value),
                lastupdated: record?.value?.lastupdated,
                createdon: record?.value?.createdon,
                shipment: record?.value?.metadata?.shipmentname,
                alertgroup: record?.value?.metadata["contact-group"],

                lastComment:
                  record?.value?.metadata?.audit?.[
                    record.value?.metadata.audit.length - 1
                  ]?.comment,
              })
            }
            }
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
            {subject}
          </button>
        );
      },
    },
    ...(user?.role?.permissions?.devices?.view
      ? [
        {
          title: (
            <DropdownFilterWithSearchAndCheckbox
              text="Device"
              isSortable={true}
              onApply={handleDeviceFilterChange}
              onReset={handleDeviceFilterReset}
              checkboxItems={
                  deviceId &&  devices.length > 0
                  ? devices.map((device) => ({
                    text: device,
                    value:
                      deviceId?.toLowerCase() === device?.toLowerCase(),
                  }))
                  : deviceId
                    ? [{ text: deviceId, value: true }]
                    : deviceName
                      ? [{ text: deviceName, value: true }]
                      : []
              }
            />
          ),
          dataIndex: "device",
          key: "device",
          width: "15%",
          render: (text, record) => (
            <Link
              to={`/devices/${record.deviceid}/map`}
              className="link-alarms"
            >
              {text}
            </Link>
          ),
        },
      ]
      : []),
    {
      title: (
        <DropdownFilterWithSearch
         accountId={user?.accountId}
          text="Associations"
          value={filters?.associationname || ""}
          isSortable={false}
          onApply={handleAssociationSearch}
          onReset={handleAssosciationSearchReset}
        />
      ),
      dataIndex: "associationname",
      key: "associationname",
      width: "15%",
      className: "alert-organization",
      render: (text,record) => (<Link
                      className="link-alarms"
                      to={
                        record?.value?.metadata?.["service-type"] === "inventory"
                          ? (record?.value?.metadata?.assetid 
                              && record?.value?.metadata?.assetid !== "N/A"
                              && record?.value?.metadata?.assetid
                              ? `/assets/${record?.value?.metadata?.assetid}`
                              : `/assets`)
                          : (record?.value?.metadata?.shipmentid 
                              && record?.value?.metadata.shipmentid !== "N/A"
                              ? `/viewshipment/${record?.value?.metadata?.shipmentid}`
                              : `/shipments`)
                      }>{text && text?.startsWith("{{$") ? "Unknown" : text}</Link>),
    },
    {
      title: (
        <DropdownFilterWithDateRangePicker
         accountId={user?.accountId}
          text="Last updated"
          isSortable={true}
          onApply={handleLastUpdatedFilterChange}
          onReset={handleLastUpdatedFilterReset}
        />
      ),
      dataIndex: "lastupdated",
      className: "alert-lastupdated",
      width: "15%",
      render: (text) => <span>{dateFormatWithAt(text)}</span>,
      key: "lastupdated",
    },

    {
      title: (
        <Dropdown
          overlay={
            <Menu>
              {/* Additional filter removed from the dropdown */}
              <Menu.Item>
                <div style={{ minWidth: 170 }}>
                  <div>
                    <span className="font-face-vzw fw-bold">Actions</span>
                    {user?.role?.permissions?.alarms?.edit && (
                      <div
                        className="mt-2 cursor-pointer"
                        onClick={() =>
                          selectedItems.length > 0
                            ? bulkeditAlerts({
                              subject: `${filters.subject}`,
                              level: `${filters.level}`,
                            })
                            : undefined
                        }
                      >
                        Bulk edit
                      </div>
                    )}
                    {user?.role?.permissions?.alarms?.download && (
                      <div
                        className="mt-2 cursor-pointer"
                        onClick={handleDownload}
                      >
                        Export
                      </div>
                    )}
                  </div>
                </div>
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <button
            className="btn btn-link text-secondary"
            onClick={(e) => e.preventDefault()}
          >
            <i className="bi-three-dots-vertical fs-6" />
          </button>
        </Dropdown>
      ),
      dataIndex: "action",
      width: "5%",
    },
  ];
  const handlePageSizeChange = (page, pageSize) => {
    dispatch(
      updatePaginationRequest({
        data: { pageSize: pageSize, currentPage: page },
        shouldUpdate: true,
      })
    );
    if(filters.limitnumber !== pageSize){
   dispatch(fetchRequest({ ...filters, limitnumber: pageSize, pagenumber: page - 1 }));
   }
  };

  return (
    <div
      style={{
        marginTop: "20px",
        backgroundColor: "#fff",
        borderRadius: "12px",
      }}
    >
      {notification.isActive && (
        <ToastNotification
          type={notification.type}
          message={notification.message}
          description={notification.description}
          duration={1}
          style={notification.style}
        />
      )}
      {showNotification && (
        <ToastNotification
          type={notificationProps.type}
          message={notificationProps.message}
          description={notificationProps.description}
          duration={notificationProps.duration}
          style={notificationProps.style}
        />
      )}

      <div>
        <AlertsDetails
          visible={isPopupVisible}
          closePopup={() => setIsPopupVisible(false)}
          setSelectedItems={setSelectedItems}
          setSelectedStatuses={setSelectedStatuses}
        />
        <BulkDetails
          visible={isbulkPopupVisible}
          selectedItems={selectedItems}
          selectedStatuses={selectedStatuses}
          setSelectedItems={setSelectedItems}
          setSelectedStatuses={setSelectedStatuses}
          closePopup={() => setIsBulkPopupVisible(false)}
        />
      </div>

      <Table
        rowKey="key"
        loading={isLoading}
        columns={columns}
        dataSource={tableData}
        onChange={handleTableChange}
        style={{
          width: "100%",
          minWidth: "1200px",
          overflowX: "auto",
        }}
        pagination={{
          current: filters.pagenumber + 1,
          pageSize: filters.limitnumber,
          total: alertCounts.total,
          showTotal: (total, range) =>
            `Showing ${range[0]}-${range[1]} of ${total}`,
          position: ["bottomRight"],
          prevIcon: "Previous",
          nextIcon: "Next",
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page, pageSize) => handlePageSizeChange(page, pageSize),
          showSizeChanger: true,
          locale: {
            items_per_page: "",
          },
        }}

      />
    </div>
  );
};

export default AlertsList;
